import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Gift, 
  QrCode, 
  TrendingUp, 
  Store, 
  Users, 
  Shield,
  ChevronRight,
  Star
} from 'lucide-react';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0C15] text-white overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="gradient-orb gradient-orb-primary w-[600px] h-[600px] -top-[200px] -left-[200px]" />
        <div className="gradient-orb gradient-orb-secondary w-[500px] h-[500px] top-[40%] -right-[150px]" />
        <div className="gradient-orb gradient-orb-accent w-[400px] h-[400px] bottom-[10%] left-[20%]" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <span className="font-outfit text-xl font-bold tracking-tight">PerkflowApp</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login">
            <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="login-nav-btn">
              Iniciar Sesión
            </Button>
          </Link>
          <Link to="/register">
            <Button className="btn-premium bg-indigo-600 hover:bg-indigo-500 text-white" data-testid="register-nav-btn">
              Comenzar Gratis
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-8 pt-20 pb-32 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-sm">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-white/80">Plataforma #1 de Fidelización</span>
            </div>
            
            <h1 className="font-outfit text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Fideliza a tus clientes con
              <span className="text-gradient block">recompensas premium</span>
            </h1>
            
            <p className="text-lg text-white/60 max-w-lg leading-relaxed">
              Sistema SaaS multitenant para crear programas de lealtad personalizados. 
              Cada comercio con su propia identidad, niveles, premios y promociones.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button className="btn-premium bg-indigo-600 hover:bg-indigo-500 text-white h-12 px-8" data-testid="hero-cta-btn">
                  Crear mi programa
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="btn-premium border-white/20 text-white hover:bg-white/10 h-12 px-8" data-testid="hero-demo-btn">
                  Ver demo
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-8 pt-4">
              <div>
                <div className="text-3xl font-outfit font-bold">500+</div>
                <div className="text-sm text-white/50">Comercios activos</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <div className="text-3xl font-outfit font-bold">50k+</div>
                <div className="text-sm text-white/50">Clientes fidelizados</div>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div>
                <div className="text-3xl font-outfit font-bold">1M+</div>
                <div className="text-sm text-white/50">Puntos canjeados</div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative">
            <div className="glass-card p-6 rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=1200&q=80"
                alt="Empleado de cafetería atendiendo a un cliente con café"
                className="w-full h-80 object-cover rounded-xl"
              />
              <div className="absolute bottom-10 left-10 right-10 glass p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-white/50 uppercase tracking-wider">Nivel Actual</div>
                    <div className="text-lg font-outfit font-bold text-amber-400">Gold Member</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/50 uppercase tracking-wider">Puntos</div>
                    <div className="text-2xl font-outfit font-bold">2,450</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-8 py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight mb-4">
              Todo lo que necesitas para fidelizar
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              Una plataforma completa con todas las herramientas para crear y gestionar 
              programas de lealtad profesionales.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: QrCode,
                title: "Escaneo QR",
                description: "Registra visitas y canjea premios con un simple escaneo desde la tablet del local."
              },
              {
                icon: TrendingUp,
                title: "Niveles y Progresión",
                description: "Sistema de niveles configurable que motiva a los clientes a seguir acumulando puntos."
              },
              {
                icon: Gift,
                title: "Premios Personalizados",
                description: "Crea premios únicos para cada nivel. Desde descuentos hasta experiencias exclusivas."
              },
              {
                icon: Store,
                title: "Branding Dinámico",
                description: "Cada comercio con su propia identidad visual: colores, logos y temas personalizados."
              },
              {
                icon: Users,
                title: "Gestión de Equipo",
                description: "Roles diferenciados: administradores, empleados y clientes con permisos específicos."
              },
              {
                icon: Shield,
                title: "Anti-Fraude",
                description: "Sistema de validaciones y controles para evitar abusos y garantizar la integridad."
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="glass-card p-6 rounded-xl card-hover group"
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-600/20 flex items-center justify-center mb-4 group-hover:bg-indigo-600/30 transition-colors">
                  <feature.icon className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="font-outfit text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-8 py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-outfit text-3xl lg:text-4xl font-bold tracking-tight mb-6">
            Comienza a fidelizar clientes hoy
          </h2>
          <p className="text-white/50 mb-8 max-w-2xl mx-auto">
            Crea tu cuenta gratuita y configura tu primer programa de lealtad en minutos. 
            Sin compromisos, cancela cuando quieras.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="btn-premium bg-indigo-600 hover:bg-indigo-500 text-white h-12 px-8" data-testid="cta-register-btn">
                Crear cuenta gratis
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="btn-premium border-white/20 text-white hover:bg-white/10 h-12 px-8" data-testid="cta-login-btn">
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-8 py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Gift className="w-4 h-4 text-white" />
            </div>
            <span className="font-outfit font-bold">PerkflowApp</span>
          </div>
          <p className="text-sm text-white/40">
            &copy; 2024 PerkflowApp. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
